import { Link, useParams } from "@tanstack/react-router";

import { useRefillSession } from "@/lib/api/refill-sessions";
import { useRefillSteps } from "@/lib/api/refill-steps";
import { usePendingActionsForGrow } from "@/lib/api/pending-actions";
import { useRefillConversation } from "@/lib/refill/conversation";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ErrorState } from "@/components/error-state";
import { AuditLogViewer } from "@/components/audit-log-viewer";
import { StepList } from "@/components/refill/step-list";
import { TrajectoryChart } from "@/components/refill/trajectory-chart";
import { SafetyViolationBanner } from "@/components/refill/safety-violation-banner";
import { ManualModeBanner } from "@/components/refill/manual-mode-banner";
import { AIPanel } from "@/components/refill/ai-panel";
import { SessionSummaryCard } from "@/components/refill/session-summary-card";
import { UrgencyBadge } from "@/components/urgency-badge";
import { Button } from "@/components/ui/button";
import { usePreferences } from "@/lib/preferences";
import { formatTemp, tempUnitLabel } from "@/lib/units";
import type { PendingAction } from "@/lib/api/types";

// Refill detail — the read-only record + audit trail for a session. The
// guided 8-step capture/mix/pour flow lives in the wizard
// (/refills/$sessionId/wizard); this page links into it while a session is
// still open.

export function RefillDetailPage() {
  const { sessionId } = useParams({ from: "/refills/$sessionId" });
  const sess = useRefillSession(sessionId);
  const steps = useRefillSteps(sessionId);
  // The same continuous conversation the wizard built — seeded read-only here
  // from the server-persisted thread (llm_calls), so the analysis / prioritized
  // actions the grower ran show up in the record, not just in the live wizard.
  const conv = useRefillConversation(sessionId);
  // Pending actions this refill generated (derived_from_refill_id), including
  // resolved ones, so the session shows what it produced.
  const generated = usePendingActionsForGrow(sess.data?.grow_id, true);

  if (sess.isLoading)
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (sess.error)
    return <ErrorState error={sess.error} retry={sess.refetch} />;
  if (!sess.data) return null;
  const s = sess.data;
  const fromThisRefill = (generated.data?.items ?? []).filter(
    (a) => a.derived_from_refill_id === s.id,
  );

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">
            Refill session
          </h1>
          <p className="text-sm text-muted-foreground">
            started {formatTime(s.started_at)} · trigger{" "}
            <span className="font-medium">{s.trigger}</span>
            {s.decision_type && ` · ${s.decision_type}`}
            {s.closed_at && " · closed"}
            {s.voided_at && ` · voided: ${s.voided_reason ?? ""}`}
          </p>
        </div>
        {!s.closed_at && !s.voided_at && (
          <Link
            to="/refills/$sessionId/wizard"
            params={{ sessionId: s.id }}
            search={{ step: 1 }}
          >
            <Button size="sm">Open guided wizard</Button>
          </Link>
        )}
      </header>

      <SessionSummaryCard session={s} formatTime={formatTime} />

      <section className="grid gap-4 md:grid-cols-3">
        <StateCard title="pre" state={s.pre_state} />
        <StateCard title="pre-pour" state={s.pre_pour_state} />
        <StateCard title="post-pour" state={s.post_pour_state} />
      </section>

      <SafetyViolationBanner violations={[]} />

      {/* Manual-mode banner shows when AI is unavailable; for the MVP we
          surface it only when the session has no AI-actor steps but is
          otherwise progressing — full wiring lands when the dispatcher
          plumbs its fallback state into the API. */}
      {sess.data?.notes?.toLowerCase().includes("[manual mode]") && (
        <ManualModeBanner reason="manual_toggle" />
      )}

      <section className="space-y-2">
        <h2 className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
          Trajectory
        </h2>
        {steps.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading steps…</p>
        ) : (
          <TrajectoryChart steps={steps.data?.items ?? []} />
        )}
      </section>

      <section className="space-y-2">
        <h2 className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
          Mix &amp; pour steps
        </h2>
        <StepList sessionID={s.id} steps={steps.data?.items ?? []} />
      </section>

      <section className="space-y-2">
        <h2 className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
          AI analysis &amp; conversation
        </h2>
        <AIPanel conv={conv} />
      </section>

      {fromThisRefill.length > 0 && (
        <section className="space-y-2">
          <h2 className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
            Actions from this refill
          </h2>
          <ul className="divide-y rounded-md border">
            {fromThisRefill.map((a) => (
              <GeneratedActionRow key={a.id} action={a} />
            ))}
          </ul>
        </section>
      )}

      <section className="space-y-2">
        <h2 className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
          Audit trail
        </h2>
        <AuditLogViewer entityType="refill_session" entityID={s.id} />
      </section>
    </div>
  );
}

// GeneratedActionRow is a compact, read-only view of a pending action this
// refill produced (open or resolved), so the session record shows what it led
// to — closing the "I got pending-action recommendations but they're nowhere
// on the session" gap.
function GeneratedActionRow({ action }: { action: PendingAction }) {
  const resolved = action.resolution ?? (action.dismissed_at ? "dismissed" : null);
  return (
    <li className="space-y-1 px-4 py-3">
      <div className="flex flex-wrap items-center gap-2">
        <UrgencyBadge urgency={action.urgency} />
        <span className="font-medium">{action.title}</span>
        {resolved ? (
          <span
            className={
              "text-xs " +
              (resolved === "completed"
                ? "text-emerald-600 dark:text-emerald-400"
                : "text-muted-foreground")
            }
          >
            {resolved}
          </span>
        ) : (
          <span className="text-xs text-amber-600 dark:text-amber-400">open</span>
        )}
      </div>
      {action.rationale && (
        <p className="text-sm text-muted-foreground">{action.rationale}</p>
      )}
    </li>
  );
}

import type { ReservoirState } from "@/lib/api/types";

function StateCard({
  title,
  state,
}: {
  title: string;
  state: ReservoirState | undefined;
}) {
  const { temperatureUnit } = usePreferences();
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm font-medium text-muted-foreground capitalize">
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-1 text-sm">
        {!state ? (
          <p className="text-muted-foreground">not captured</p>
        ) : (
          <>
            <Row label="pH" v={state.ph} />
            <Row label="EC" v={state.ec} />
            <Row
              label="temp"
              v={state.temp_c}
              suffix={tempUnitLabel(temperatureUnit)}
              format={(c) => formatTemp(c, temperatureUnit, 1)}
            />
            <Row label="level" v={state.level_gal} suffix="gal" />
          </>
        )}
      </CardContent>
    </Card>
  );
}

function Row({
  label,
  v,
  suffix,
  format,
}: {
  label: string;
  v: number | undefined;
  suffix?: string;
  format?: (n: number) => string;
}) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-muted-foreground">{label}</span>
      <span className="tabular-nums">
        {v === undefined
          ? "—"
          : `${format ? format(v) : v}${suffix ? " " + suffix : ""}`}
      </span>
    </div>
  );
}

function formatTime(s: string): string {
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleString();
}
